const STORAGE_KEY = 'montilla:order-session'

export type OrderStatus = 'browsing' | 'cart' | 'checkout' | 'payment' | 'confirmed'

export interface CartItem {
  product_id: string
  name: string
  price: number
  quantity: number
  currency?: string
  category?: string
  variant?: string
  url?: string
  product_image_url?: string
}

interface CurrentOrder {
  orderId?: string
  status: OrderStatus
  cart: Record<string, CartItem>
  updatedAt: number
}

interface OrderSessionData {
  current: CurrentOrder
  confirmedOrders: string[]
}

function emptyOrder(): CurrentOrder {
  return { status: 'browsing', cart: {}, updatedAt: Date.now() }
}

/**
 * Persists the current order session to localStorage.
 * Tracks the full purchase lifecycle (browsing → cart → checkout → payment → confirmed)
 * with an embedded cart, and keeps a list of confirmed order IDs for deduplication.
 *
 * Survives page reloads, SPA navigations, and new tabs.
 * Used by the event-bus and destinations for ALL providers.
 */
class OrderSessionStoreImpl {
  private data: OrderSessionData

  constructor() {
    this.data = this.restore()
  }

  // ── Cart ──

  addItem(item: CartItem): void {
    const existing = this.data.current.cart[item.product_id]
    if (existing) {
      existing.quantity = item.quantity ?? (existing.quantity + 1)
      existing.price = item.price ?? existing.price
      existing.name = item.name ?? existing.name
      if (item.currency) existing.currency = item.currency
      if (item.category) existing.category = item.category
      if (item.variant) existing.variant = item.variant
      if (item.url) existing.url = item.url
      if (item.product_image_url) existing.product_image_url = item.product_image_url
    } else {
      this.data.current.cart[item.product_id] = { ...item }
    }

    if (this.data.current.status === 'browsing') {
      this.data.current.status = 'cart'
    }

    this.touch()
  }

  removeItem(productId: string, quantity?: number): void {
    if (!quantity) {
      const { [productId]: _, ...rest } = this.data.current.cart
      this.data.current.cart = rest
      this.touch()
      return
    }

    const existing = this.data.current.cart[productId]
    if (existing) {
      existing.quantity -= quantity
      if (existing.quantity <= 0) {
        const { [productId]: _, ...rest } = this.data.current.cart
        this.data.current.cart = rest
      }
    }
    this.touch()
  }

  getItem(productId: string): CartItem | undefined {
    return this.data.current.cart[productId]
  }

  getCartItems(): CartItem[] {
    return Object.values(this.data.current.cart)
  }

  getCartSnapshot(): { items: CartItem[]; total: number; item_count: number } {
    const items = this.getCartItems()
    return {
      items,
      total: items.reduce((sum, item) => sum + (item.price * item.quantity), 0),
      item_count: items.reduce((sum, item) => sum + item.quantity, 0),
    }
  }

  get isCartEmpty(): boolean {
    return Object.keys(this.data.current.cart).length === 0
  }

  get cartSize(): number {
    return Object.keys(this.data.current.cart).length
  }

  clearCart(): void {
    this.data.current.cart = {}
    this.touch()
  }

  /**
   * Replace the whole cart with a snapshot (e.g. a `checkout_view` carrying the
   * full `items[]`). Unlike {@link addItem} (incremental), this makes the store
   * mirror exactly the given items — so a page that only sends the entire cart
   * (no per-item add) keeps the session cart accurate for the order lifecycle
   * and cross-provider consumers. Empty input is a no-op so a cart-less event
   * never wipes an existing cart.
   */
  setCart(items: CartItem[]): void {
    if (!items.length) return
    const cart: Record<string, CartItem> = {}
    for (const item of items) {
      if (!item?.product_id) continue
      cart[item.product_id] = { ...item, quantity: item.quantity ?? 1 }
    }
    if (Object.keys(cart).length === 0) return
    this.data.current.cart = cart
    if (this.data.current.status === 'browsing') {
      this.data.current.status = 'cart'
    }
    this.touch()
  }

  // ── Order lifecycle ──

  getOrderId(): string | null {
    return this.data.current.orderId ?? null
  }

  getStatus(): OrderStatus {
    return this.data.current.status
  }

  /**
   * Advance the current order to a new status.
   * Returns false if the same orderId+status was already sent (dedup).
   */
  transition(orderId: string, status: OrderStatus): boolean {
    if (status === 'confirmed' && this.isOrderConfirmed(orderId)) {
      return false
    }

    // Block duplicate: same orderId and same status as current
    if (this.data.current.orderId === orderId && this.data.current.status === status) {
      return false
    }

    this.data.current.orderId = orderId
    this.data.current.status = status

    if (status === 'confirmed') {
      this.data.confirmedOrders.push(orderId)
      // Reset current order for next purchase
      this.data.current = emptyOrder()
    }

    this.touch()
    return true
  }

  /** Check if an orderId was already confirmed in this session */
  isOrderConfirmed(orderId: string): boolean {
    return !!orderId && this.data.confirmedOrders.includes(orderId)
  }

  getConfirmedOrders(): readonly string[] {
    return this.data.confirmedOrders
  }

  // ── Lifecycle ──

  /** @internal Snapshot for debugging / extension */
  getSnapshot(): Readonly<OrderSessionData> {
    return JSON.parse(JSON.stringify(this.data))
  }

  reset(): void {
    this.data = { current: emptyOrder(), confirmedOrders: [] }
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch { /* localStorage unavailable */ }
  }

  // ── Private ──

  private touch(): void {
    this.data.current.updatedAt = Date.now()
    this.persist()
  }

  private restore(): OrderSessionData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return { current: emptyOrder(), confirmedOrders: [] }
      const parsed = JSON.parse(raw) as OrderSessionData
      if (!parsed?.current?.cart) return { current: emptyOrder(), confirmedOrders: parsed?.confirmedOrders ?? [] }
      return parsed
    } catch {
      return { current: emptyOrder(), confirmedOrders: [] }
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data))
    } catch { /* localStorage unavailable */ }
  }
}

let instance: OrderSessionStoreImpl | null = null

export function getOrderSessionStore(): OrderSessionStoreImpl {
  if (!instance) {
    instance = new OrderSessionStoreImpl()
  }
  return instance
}

/** @internal Reset singleton for testing */
export function resetOrderSessionStore(): void {
  instance = null
}
